import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, X, BookOpen, ChevronDown, ChevronUp, BookMarked, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import useGuides from '../hooks/useGuides';

/**
 * HelpDrawer — the single help surface on the app.
 *
 * Mounted globally in `DashboardLayout`. Floating-action-button bottom-right
 * opens a drawer with: (1) page-specific overview & step list, (2) link to
 * the full `/user-guide` page, (3) launcher for `HelpGuide` walkthroughs.
 *
 * `HelpGuide` (the step-by-step overlay) is intentionally a separate
 * component — it's the *player* while this drawer is the *catalog*. They
 * are complementary, not overlapping. Iter-209 (D-3) consolidated the
 * help surface by deleting the unused `HelpBanner` (dead code) and
 * pinning that the surviving pair is the canonical pattern.
 *
 * All visible text is scoped to the current user's role via `useGuides`.
 */
export default function HelpDrawer({ title, overview, steps, onStartGuide, autoOpenGuides }) {
  const { user } = useAuth();
  const userRole = user?.role;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [exp, setExp] = useState(false);
  const [sg, setSg] = useState(false);
  const ref = useRef(null);
  const GUIDES = useGuides();
  const walkList = Object.values(GUIDES).map(g => ({ id: g.id, t: g.title, d: g.description, s: g.steps.length }));
  const walkCount = walkList.length;

  useEffect(() => {
    if (autoOpenGuides) { setOpen(true); setSg(true); return; }
  }, [autoOpenGuides]);
  useEffect(() => { if (!open) { setExp(false); setSg(false); } }, [open]);
  useEffect(() => { if (!autoOpenGuides) { setExp(false); setSg(false); setOpen(false); } }, [title, autoOpenGuides]);
  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) { const fab = document.querySelector('[data-testid="help-fab"]'); if (fab && fab.contains(e.target)) return; setOpen(false); } };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);
  return (
    <React.Fragment>
      <button data-testid="help-fab" onClick={() => setOpen(!open)} className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${open ? 'bg-sky-500 text-white shadow-sky-500/30 scale-95' : 'bg-sky-500/15 text-sky-400 border border-sky-500/30 hover:bg-sky-500/25 hover:scale-110'}`} title="Page Help">
        {open ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
      </button>
      {open && (
        <div ref={ref} data-testid="help-drawer" className="fixed bottom-20 right-6 z-40 w-96 max-h-[75vh] overflow-hidden rounded-xl shadow-2xl border border-sky-500/15" style={{ background: 'hsl(222 47% 8% / 0.97)', backdropFilter: 'blur(20px)', animation: 'hdu .25s cubic-bezier(.16,1,.3,1) forwards' }}>
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center"><BookOpen className="w-4 h-4 text-sky-400" /></div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-sky-400 truncate flex-1 min-w-0" style={{ fontFamily: 'Barlow Condensed' }}>{title || 'Page Help'}</h3>
            <span
              data-testid="help-drawer-role-scope"
              title="Help content is scoped to what your role can access"
              className="flex-shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-300/80 border border-sky-500/20"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              scoped to <b className="capitalize">{userRole || 'guest'}</b>
            </span>
          </div>
          <div className="overflow-y-auto max-h-[calc(75vh-60px)] px-5 py-4 space-y-4">
            <button
              data-testid="help-open-user-guide"
              onClick={() => { setOpen(false); navigate('/user-guide'); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-sky-500/15 to-violet-500/15 hover:from-sky-500/25 hover:to-violet-500/25 border border-sky-500/25 hover:border-sky-500/40 transition-all group"
            >
              <div className="w-9 h-9 rounded-lg bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                <BookMarked className="w-4 h-4 text-sky-300" />
              </div>
              <div className="min-w-0 flex-1 text-left">
                <p className="text-sm font-semibold text-foreground group-hover:text-sky-300">Open Full User Guide</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Complete operational manual · every role, every workflow.</p>
              </div>
              <ArrowRight className="w-4 h-4 text-sky-400 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </button>
            {title ? (
              <React.Fragment>
                <p className="text-sm text-foreground/80 leading-relaxed">{overview}</p>
                {steps && steps.length > 0 && (
                  <React.Fragment>
                    <button data-testid="help-steps-toggle" onClick={() => setExp(!exp)} className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300 uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>
                      {exp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {exp ? 'Hide' : 'Show'} Step-by-Step Guide
                    </button>
                    {exp && <div className="space-y-3 pb-2">{steps.map((s, i) => <div key={`step-${i}`} className="flex items-start gap-3"><span className="flex-shrink-0 w-6 h-6 rounded-full bg-sky-500/10 text-sky-400 flex items-center justify-center text-[10px] font-bold mt-0.5">{i + 1}</span><p className="text-sm text-muted-foreground leading-relaxed">{s}</p></div>)}</div>}
                  </React.Fragment>
                )}
              </React.Fragment>
            ) : <p className="text-sm text-muted-foreground">No help content available for this page.</p>}
            <div className="pt-2 border-t border-white/5">
              <button data-testid="help-guides-toggle" onClick={() => setSg(!sg)} className="flex items-center gap-2 text-xs uppercase tracking-wider w-full" style={{ fontFamily: 'Barlow Condensed' }}>
                <BookOpen className="w-3.5 h-3.5 text-amber-400" /><span className="text-amber-400">Guided Walkthroughs</span>
                <span
                  data-testid="help-guides-count"
                  className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300/80 border border-amber-500/20 normal-case tracking-normal"
                >
                  {walkCount} for {userRole || 'guest'}
                </span>
                {sg ? <ChevronUp className="w-3.5 h-3.5 text-amber-400 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 text-amber-400 ml-auto" />}
              </button>
              {sg && <div className="mt-3 space-y-2">{walkList.length === 0 ? (
                <p className="text-[11px] text-muted-foreground px-1 py-2">No guided walkthroughs available for your role.</p>
              ) : walkList.map(g => (
                <button key={g.id} data-testid={`start-guide-${g.id}`} onClick={() => { setOpen(false); onStartGuide?.(g.id); }} className="w-full flex items-center gap-3 px-3 py-3 rounded-lg bg-white/[0.02] hover:bg-sky-500/10 border border-transparent hover:border-sky-500/15 transition-all group text-left">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-sky-500/10"><BookOpen className="w-4 h-4 text-amber-400 group-hover:text-sky-400" /></div>
                  <div className="min-w-0 flex-1"><p className="text-sm font-medium text-foreground/90 group-hover:text-sky-400">{g.t}</p><p className="text-[11px] text-muted-foreground mt-0.5">{g.d}</p></div>
                  <span className="text-[10px] text-muted-foreground">{g.s} steps</span>
                </button>
              ))}</div>}
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes hdu{from{opacity:0;transform:translateY(16px) scale(.96)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </React.Fragment>
  );
}
